import { Fragment, useMemo, useState } from "react";
import DataTable from "../../Components/DataTable";
import { createColumnHelper } from "@tanstack/react-table";
import Swal from "sweetalert2/dist/sweetalert2.js";
import "sweetalert2/dist/sweetalert2.min.css";
import { API_BASE_URL } from "../../api/api";
import { StatusChip } from "../common/StatusChip";

export const CountriesAvailableList = ({ data, isLoading, onToggleStatus, onEditCountry }) => {
  const columnHelper = createColumnHelper();

  const columns = useMemo(() => [
    columnHelper.accessor("name", { header: "Nombre del país" }),

    columnHelper.display({
      id: "paymentGateways",
      header: "Pasarelas de pago",
      cell: (props) => {
        const gateways = props.row.original.paymentGateways || [];
        return gateways.length > 0 ? (
          <div className="d-flex flex-wrap gap-1">
            {gateways.map((gw, idx) => (
              <StatusChip key={idx} tone="neutral" sm>{gw}</StatusChip>
            ))}
          </div>
        ) : (
          <span>Sin pasarelas</span>
        );
      },
    }),

    columnHelper.accessor("currency", {
      header: "Moneda local"
    }),

    columnHelper.accessor("pointValue", {
      header: "Valor Puntos",
      cell: (info) => `$ ${info.getValue()?.toLocaleString() ?? 0}`,
    }),

    columnHelper.display({
      id: "status",
      header: "Estado",
      cell: (props) => {
        const status = props.row.original.status;
        return <StatusChip tone={status ? "success" : "danger"}>{status ? "Activo" : "Inactivo"}</StatusChip>;
      },
    }),
    columnHelper.display({
      id: "files",
      header: "Archivos",
      cell: (props) => {
        const country = props.row.original;

        const files = [
          { key: "sanitaryRegistration", label: "Registro Sanitario" },
          { key: "personalDataAuthorization", label: "Autorización Datos" },
          { key: "termsAndConditions", label: "Términos y Condiciones" },
        ];

        return (
          <div className="d-flex flex-column gap-1">
            {files.map(({ key, label }) =>
              country[key] ? (
                <a
                  key={key}
                  href={`${API_BASE_URL}/product/countries-available/get-file/${country[key]}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-primary d-flex align-items-center"
                  download
                  title={`Descargar ${label}`}
                >
                  <i className="fa fa-download me-1"></i>
                  <small>{label}</small>
                </a>
              ) : (
                <small key={key} className="text-muted">
                  Sin {label}
                </small>
              )
            )}
          </div>
        );
      },
    }),


    columnHelper.accessor("created_at", {
      header: "Fecha creación",
      cell: (info) => new Date(info.getValue()).toLocaleDateString(),
    }),

    columnHelper.display({
      id: "actions",
      header: "Acciones",
      cell: (props) => {
        const country = props.row.original;
        const isActive = country.status;
        const toggleClass = isActive ? "ui-icon-btn ui-icon-btn--danger" : "ui-icon-btn ui-icon-btn--accent";
        const toggleIcon = isActive ? "ri-forbid-line" : "ri-checkbox-circle-line";
        const toggleText = isActive ? "Inactivar" : "Activar";

        const handleEdit = () => {
          onEditCountry(country);
        };

        const handleToggle = () => {
          Swal.fire({
            title: `¿Quieres ${toggleText} este país?`,
            text: `País: ${country.name}`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: isActive ? "#DC2626" : "#4B36F0",
            cancelButtonColor: "#6c757d",
            confirmButtonText: `Sí, ${toggleText}`,
            cancelButtonText: "Cancelar",
          }).then((result) => {
            if (result.isConfirmed) {
              onToggleStatus(country.id);
            }
          });
        };

        return (
          <div className="d-flex gap-2 justify-content-end">
            <button
              type="button"
              className="ui-icon-btn"
              onClick={handleEdit}
              title="Editar"
            >
              <i className="ri-edit-line" />
            </button>

            <button
              type="button"
              className={toggleClass}
              onClick={handleToggle}
              title={toggleText}
            >
              <i className={toggleIcon} />
            </button>
          </div>
        );
      },
    }),
  ], [onToggleStatus, onEditCountry]);

  const rows = data ?? [];

  // Lista embebida, sin paginación propia del backend: pagina en cliente
  // sobre lo ya cargado, sin duplicar el fetch.
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const pageData = rows.slice(
    pagination.pageIndex * pagination.pageSize,
    (pagination.pageIndex + 1) * pagination.pageSize
  );

  return (
    <Fragment>
      <DataTable
        data={pageData}
        total={rows.length}
        pagination={pagination}
        setPagination={setPagination}
        columns={columns}
        progressPending={isLoading}
        minWidth={900}
        emptyMessage="No hay países disponibles registrados."
      />
    </Fragment>
  );
};
